import React, { Fragment, useId } from 'react';
import { t, Trans } from '@lingui/macro';
import styled from 'styled-components';

import { SEARCH_PAGE_Z_INDEX } from '@rover/constants/src/search/searchPage.constants';
import Close from '@rover/icons/svg/close.svg';
import { Box, Button } from '@rover/kibble/core';
import { Modal } from '@rover/react-lib/src/components';
import { Alignment, HorizontalLayout } from '@rover/react-lib/src/components/layouts';
import Form from '@rover/react-lib/src/components/utils/Form';
import { AdvancedFiltersCollection } from '@rover/react-lib/src/pages/search/SearchPage/components';
import AdvancedFiltersCollectionContainerV2 from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Collections/AdvancedFiltersCollectionContainerV2';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import { useSimplifiedFilters } from '../../../utilities/useSimplifiedFilters';

export type Props = {
  getServiceTypeDisplayName: (arg0: string) => string;
  isOpen: boolean;
  onClose: (scrollToResults: boolean) => void;
  searchFilters: SearchFilters;
  updateFiltersAndFireSearchOnChange: (arg0: Partial<SearchFilters>) => void;
  className?: string;
  inVariantSimplifiedMobileOwnerSearchFormExperiment?: boolean;
  isRollout1?: boolean;
};
const HeaderOverlay = styled(HorizontalLayout)<{ isOpen: boolean; className?: string }>`
  visibility: ${({ isOpen }) => (isOpen ? 'inherit' : 'hidden')};
  background-color: white;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
  z-index: ${SEARCH_PAGE_Z_INDEX.ADVANCED_FILTER_MODAL.toString()};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: ${({ theme }) => theme.space['2x']};
`;
const HeaderTitle = styled.span`
  font-size: ${({ theme }) => theme.heading['300'].fontSize};
  font-weight: ${({ theme }) => theme.heading['300'].fontWeight};
`;
const ModalContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: ${({ theme }) => `56px ${theme.space['4x']} 0px ${theme.space['4x']}`};
  overflow: auto;
`;
const ApplyButton = styled(Button)<{
  isOpen: boolean;
}>`
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  position: fixed;
  bottom: ${({ theme }) => theme.space['6x']};
  left: 50%;
  transform: translateX(-50%);
  z-index: ${SEARCH_PAGE_Z_INDEX.ADVANCED_FILTER_MODAL.toString()};
  width: 70%;
  max-width: 300px;
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
  tabindex: ${({ isOpen }) => (isOpen ? 0 : -1)};
`;

const AdvancedFiltersModal = ({
  getServiceTypeDisplayName,
  isOpen,
  onClose,
  searchFilters,
  updateFiltersAndFireSearchOnChange,
  className,
  inVariantSimplifiedMobileOwnerSearchFormExperiment = false,
  isRollout1 = false,
}: Props): JSX.Element => {
  const handleSubmit = (filters): void => {
    onClose(true);

    updateFiltersAndFireSearchOnChange(filters);
  };

  const { isActive: isUseSimplifiedFiltersActive } = useSimplifiedFilters();

  const handleClose = (form): void => {
    form.clearData();
    onClose(false);
  };

  const initialData: SearchFilters = { ...searchFilters };
  const headerTitleId = useId();
  return (
    // TODO: Refactor this component to use Redux instead of form, like in PaginatedWizardModal - https://roverdotcom.atlassian.net/browse/DEV-43006
    <Form initialData={initialData} onSubmit={handleSubmit}>
      {(form) => (
        <Fragment>
          <HeaderOverlay
            className={className}
            isOpen={isOpen}
            hAlign={Alignment.CENTER}
            vAlign={Alignment.CENTER}
          >
            <HeaderTitle id={headerTitleId}>
              {isUseSimplifiedFiltersActive
                ? t`Filters`
                : getServiceTypeDisplayName(searchFilters.serviceType)}
            </HeaderTitle>
            <Box position="absolute" top="1x" right="0x">
              <Button
                icon={Close}
                aria-label="Close Modal"
                variant="flat"
                size="small"
                onClick={() => handleClose(form)}
                webProps={{ tabIndex: isOpen ? 0 : -1 }}
              />
            </Box>
          </HeaderOverlay>
          <Modal
            isOpen={isOpen}
            hasCloseButton={false}
            closeTimeout={0}
            aria-labelledby={headerTitleId}
            className={className}
          >
            <ModalContentWrapper>
              {inVariantSimplifiedMobileOwnerSearchFormExperiment && isRollout1 ? (
                <AdvancedFiltersCollectionContainerV2
                  formData={form.data}
                  onChange={form.onChange}
                />
              ) : (
                <AdvancedFiltersCollection formData={form.data} onChange={form.onChange} />
              )}
            </ModalContentWrapper>
            <ApplyButton
              aria-label="Apply"
              webProps={{ className, type: 'button' }}
              onClick={() => handleSubmit(form.data)}
              variant="primary"
              isOpen={isOpen}
              width="calc(100% - 32px)"
              mx="4x"
              my="6x"
              display={['flex', 'none']}
              flexDirection="row"
              sx={{
                justifyContent: 'center',
              }}
            >
              <Trans>Apply</Trans>
            </ApplyButton>
          </Modal>
        </Fragment>
      )}
    </Form>
  );
};

export default AdvancedFiltersModal;
